import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Mood } from 'shared';
import { audioEngine } from './audioEngine';
import type {
  HeartbeatHandle,
  PlayBgmOptions,
  PlaySfxOptions,
  SfxName,
  StartHeartbeatOptions,
  StopBgmOptions,
} from './audioEngine';
import { AudioContextValue, type AudioApi } from './useAudio';

export interface AudioProviderProps {
  children: ReactNode;
}

/** 初回ジェスチャとして拾うイベント（autoplay policy を解除できる種類） */
const UNLOCK_EVENTS: ReadonlyArray<keyof DocumentEventMap> = [
  'pointerdown',
  'keydown',
  'touchstart',
];

/**
 * グローバル音エンジンの React Provider（R2 感覚層）。
 *
 * 役割:
 *  - アプリ全体をラップし、useAudio() に AudioApi を供給する。
 *  - マウント時に document へ初回ジェスチャ（pointerdown/keydown/touchstart）リスナーを
 *    1 回だけ張り、最初の操作で audioEngine.unlock() を呼ぶ（成功後リスナーは除去）。
 *  - engine.onChange を購読し、volume/mute 変化で再レンダーする。
 *
 * 既存ファイルは変更しない方針のため、本 Provider をどこでマウントするかは後続 Wave が決める。
 */
export function AudioProvider({ children }: AudioProviderProps): React.JSX.Element {
  // engine の現在値を React state へ反映（初期値は engine から取得）
  const [muted, setMutedState] = useState<boolean>(() => audioEngine.isMuted());
  const [volume, setVolumeState] = useState<number>(() => audioEngine.getMasterVolume());
  const [isUnlocked, setIsUnlocked] = useState<boolean>(() => audioEngine.isUnlocked());

  // engine.onChange 購読 → state 同期
  useEffect(() => {
    const unsubscribe = audioEngine.onChange(({ volume: v, muted: m }) => {
      setVolumeState(v);
      setMutedState(m);
    });
    // 購読開始時点の最新値で念のため同期（onChange 登録前の変化を取りこぼさない）
    setVolumeState(audioEngine.getMasterVolume());
    setMutedState(audioEngine.isMuted());
    return unsubscribe;
  }, []);

  // 初回ジェスチャで unlock。成功したらリスナーを外す
  const unlockedRef = useRef(false);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (unlockedRef.current) return;

    const handleFirstGesture = (): void => {
      audioEngine.unlock();
      if (audioEngine.isUnlocked()) {
        unlockedRef.current = true;
        setIsUnlocked(true);
        for (const evt of UNLOCK_EVENTS) {
          document.removeEventListener(evt, handleFirstGesture);
        }
      }
    };

    for (const evt of UNLOCK_EVENTS) {
      // passive: スクロール等を阻害しない。capture: 子が stopPropagation しても拾う
      document.addEventListener(evt, handleFirstGesture, { passive: true, capture: true });
    }

    return () => {
      for (const evt of UNLOCK_EVENTS) {
        document.removeEventListener(evt, handleFirstGesture, { capture: true } as EventListenerOptions);
      }
    };
  }, []);

  // ---- 安定した委譲コールバック群 ----
  const setMuted = useCallback((m: boolean) => audioEngine.setMuted(m), []);
  const setVolume = useCallback((v: number) => audioEngine.setMasterVolume(v), []);
  const toggleMuted = useCallback(() => audioEngine.setMuted(!audioEngine.isMuted()), []);
  const unlock = useCallback(() => {
    audioEngine.unlock();
    setIsUnlocked(audioEngine.isUnlocked());
  }, []);
  const playSfx = useCallback(
    (name: SfxName, opts?: PlaySfxOptions) => audioEngine.playSfx(name, opts),
    [],
  );
  const startHeartbeat = useCallback(
    (opts?: StartHeartbeatOptions): HeartbeatHandle => audioEngine.startHeartbeat(opts),
    [],
  );
  const playBgm = useCallback(
    (url: string, opts?: PlayBgmOptions) => audioEngine.playBgm(url, opts),
    [],
  );
  const stopBgm = useCallback((opts?: StopBgmOptions) => audioEngine.stopBgm(opts), []);
  const setAmbient = useCallback((mood: Mood | null) => audioEngine.setAmbient(mood), []);

  const value = useMemo<AudioApi>(
    () => ({
      muted,
      volume,
      isUnlocked,
      setMuted,
      setVolume,
      toggleMuted,
      unlock,
      playSfx,
      startHeartbeat,
      playBgm,
      stopBgm,
      setAmbient,
    }),
    [
      muted,
      volume,
      isUnlocked,
      setMuted,
      setVolume,
      toggleMuted,
      unlock,
      playSfx,
      startHeartbeat,
      playBgm,
      stopBgm,
      setAmbient,
    ],
  );

  return <AudioContextValue.Provider value={value}>{children}</AudioContextValue.Provider>;
}

export default AudioProvider;
